<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <div class="checkbox-container">
    <div class="checkbox-wrapper" :class="{ 'active-border': isChanging }">
      <input
        type="checkbox"
        :id="id"
        :checked="modelValue"
        :disabled="disabled"
        class="custom-check"
        :aria-describedby="`${id}-help`"
        :class="{ 'p-invalid': validation?.$error }"
        @change="handleChange"
        @blur="handleBlur"
      />
      <span
        class="checkbox-visual"
        :class="{
          ghost: isNotSet && effectiveValue !== null,
          'ghost-on': isNotSet && effectiveValue === true,
          'ghost-dash': isNotSet && effectiveValue === null,
        }"
        @click="handleVisualClick"
      ></span>
    </div>
    <label class="checkbox-label" :for="id">{{ label }}</label>
    <span
      v-if="validation?.$error && validation?.required?.$invalid"
      :id="`${id}-help`"
      class="p-error text-left mt-2 block"
    >
      {{ PropName }} is required.
    </span>
    <span
      v-else-if="
        validation?.$error &&
        validation?.requiredIf &&
        validation?.requiredIf.$invalid
      "
      :id="`${id}-help`"
      class="p-error text-left mt-2 block"
    >
      {{ validation.requiredIf.$message.replace("value", PropName) }}
    </span>
  </div>
</template>

<script>
export default {
  props: {
    sectionClass: {
      type: String,
      default: "",
    },
    id: {
      type: String,
      required: true,
      default: "",
    },
    label: {
      type: String,
      default: "",
    },
    validation: {
      type: Object,
      required: false,
    },
    modelValue: {
      // No `type` so tri-state mode can pass null (not-set) without warnings.
      default: false,
    },
    PropName: {
      type: String,
      default: "",
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    // Tri-state mode: on (true) / off (false) / not-set (null).
    triState: {
      type: Boolean,
      default: false,
    },
    // Inherited value shown when not-set: true => check, false => empty, null => dash.
    effectiveValue: {
      default: null,
    },
  },
  data() {
    return {
      isChanging: false, // Tracks if checkbox is being changed
    };
  },
  computed: {
    isNotSet() {
      return this.triState && this.modelValue === null;
    },
  },
  methods: {
    // on(true) -> off(false) -> not-set(null) -> on(true)
    nextTriValue() {
      if (this.modelValue === true) return false;
      if (this.modelValue === false) return null;
      return true;
    },
    handleChange(event) {
      this.isChanging = true; // Apply border during change
      if (this.triState) {
        const next = this.nextTriValue();
        this.$emit('update:modelValue', next);
        this.$emit('change', { target: { checked: next === true } });
        return;
      }
      this.$emit('update:modelValue', event.target.checked);
      this.$emit('change', event);
    },
    handleBlur() {
      this.isChanging = false; // Remove border on blur
    },
    handleVisualClick() {
      if (!this.disabled) {
        this.isChanging = true; // Apply border during change
        if (this.triState) {
          const next = this.nextTriValue();
          this.$emit('update:modelValue', next);
          this.$emit('change', { target: { checked: next === true } });
          return;
        }
        this.$emit('update:modelValue', !this.modelValue);
        this.$emit('change', { target: { checked: !this.modelValue } });
      }
    },
    handleOutsideClick(event) {
      const container = this.$el;
      if (!container.contains(event.target)) {
        this.isChanging = false; // Remove active border on outside click
      }
    },
  },
  mounted() {
    // Add global click listener for outside clicks
    document.addEventListener('click', this.handleOutsideClick);
  },
  beforeUnmount() {
    // Clean up global click listener
    document.removeEventListener('click', this.handleOutsideClick);
  },
};
</script>

<style lang="scss" scoped>
.checkbox-container {
  display: inline-flex;
  align-items: center;
}

.checkbox-wrapper {
  position: relative;
  display: inline-block;
  width: 26px; // 18px checkbox + 2px padding + 2px border (each side)
  height: 26px;
  border: 2px solid transparent; // Transparent border in inactive state
  border-radius: 8px; // Matches checkbox’s 4px radius
  padding: 2px; // 2px gap around checkbox
}

.checkbox-wrapper.active-border {
  border: 2px solid #21211f; // Matches toggle’s active-border
  border-radius: 8px;
}

.custom-check {
  appearance: none;
  -webkit-appearance: none;
  width: 0;
  height: 0;
}

.checkbox-visual {
  position: absolute;
  cursor: pointer;
  border-radius: 4px; // Matches original checkbox shape
  border: 1px solid #c0c0bc; // Default border
  background: #fff; // Default background
  transition: background-color 0.15s, border-color 0.15s;
  height: 18px; // Original checkbox size
  width: 18px;
  top: 2px; // 2px padding for gap
  right: 2px;
  bottom: 2px;
  left: 2px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.checkbox-visual::before {
  content: '';
  position: absolute;
  width: 4px; // Original checkmark size
  height: 10px;
  border: solid #fff;
  border-width: 0 1px 1px 0;
  transform: rotate(45deg);
  opacity: 0; // Hidden when unchecked
  transition: opacity 0.15s;
  margin-bottom: 3px;
}

.custom-check:checked + .checkbox-visual {
  background-color: #43a584; // Default checked state
  border-color: #348067;
}
.custom-check:checked + .checkbox-visual:hover {
  background-color: #348067; // Default checked state
  border-color: #348067;
}

.checkbox-wrapper.active-border .custom-check:checked + .checkbox-visual {
  background-color: #348067; // Checked with active outer border
  border-color: #348067;
}

.custom-check:checked + .checkbox-visual::before {
  opacity: 1; // Show checkmark when checked
}

/* Tri-state "not-set":
   - inherited ON (tenant true)  -> grayed box + grayed check
   - inherited OFF (tenant false) -> grayed empty box
   - no inherited value           -> plain box + dash (no graying) */
.checkbox-visual.ghost {
  border: 1px solid #b8b8b4;
  background: #e4e4e1;
  opacity: 0.85;
}

/* not-set + inherited ON: grayed box with a grayed check reflecting the inherited value */
.checkbox-visual.ghost-on::before {
  content: '';
  position: absolute;
  width: 4px;
  height: 10px;
  border: solid #6b6b67;
  border-width: 0 1px 1px 0;
  border-radius: 0;
  background-color: transparent;
  transform: rotate(45deg);
  top: auto;
  left: auto;
  margin-bottom: 3px;
  opacity: 1;
}

/* not-set with no inherited value: dash (—)
   Rendered as a text glyph (not a 2px bar) so the font renderer pixel-hints it
   and it looks identical on every checkbox regardless of sub-pixel box position. */
.checkbox-visual.ghost-dash::before {
  content: '\2013'; /* en dash */
  position: static;
  width: auto;
  height: auto;
  border: none;
  border-radius: 0;
  background-color: transparent;
  color: #6b6b67;
  font-size: 14px;
  font-weight: 700;
  line-height: 1;
  transform: none;
  margin: 0;
  opacity: 1;
}

// .checkbox-visual:hover .custom-check:not(:disabled) + .checkbox-visual {
//   border-color: #2d6e6d; // Hover affects only inner checkbox
//   background: #f0f0ef; // Unchecked hover background
// }

.custom-check:focus + .checkbox-visual {
  outline: none;
  border-color: #348067; // Matches toggle focus
  box-shadow: none;
}

.custom-check.p-invalid + .checkbox-visual {
  border-color: #dc3545; // Matches original invalid state
}

.custom-check:disabled + .checkbox-visual {
  background-color: #D6D6D6; // Matches toggle disabled state
  border: 1px solid #c0c0bc;
  opacity: 0.8;
}

.checkbox-visual:hover .custom-check:disabled:not(:checked) + .checkbox-visual {
  background-color: #e9ecef;
  border: 1px solid #d8d8d5;
  // opacity: 0.8;
}
.checkbox-visual:hover{
   background-color: #F0F0EF;
    border: 1px solid #D8D8D5;

}

.checkbox-visual:hover .custom-check:checked:disabled + .checkbox-visual {
  background-color: #e9ecef;
  border: 1px solid #d8d8d5;
}

.checkbox-visual:hover .custom-check:checked + .checkbox-visual {
  background-color: #348067; // Matches toggle checked hover
  border-color: #348067;
}

/* Explicitly prevent hover effects on wrapper and gap */
.checkbox-wrapper:hover {
  border: 2px solid transparent; // No change on hover
}

.checkbox-wrapper.active-border:hover {
  border: 2px solid #21211f; // Maintain active border on hover
}

.checkbox-label {
  margin-left: 0.5rem;
  vertical-align: middle;
  font-size: 1rem;
}

.p-error {
  color: #dc3545;
  font-size: 0.875rem;
}

/* Responsive adjustments */
@media screen and (max-width: 768px) {
  .checkbox-wrapper {
    width: 24px; // Slightly smaller for mobile
    height: 24px;
    padding: 1.5px; // Maintain ~2px gap
    border-radius: 7px;
  }

  .checkbox-visual {
    height: 17px; // Slightly smaller to fit
    width: 17px;
    top: 1.5px;
    right: 1.5px;
    bottom: 1.5px;
    left: 1.5px;
  }

  .checkbox-visual::before {
    width: 3.5px; // Slightly smaller checkmark
    height: 9px;
  }
}
</style>
